import { useEffect, useRef, useState } from "react";
import "./chat-ui.css";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Camera, Mic, Image, Smile, Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

/**
 * 채팅 메시지 인터페이스
 */
interface ChatMessage {
    id: string;
    text: string;
    isMe: boolean;
    timestamp: Date;
    status?: "sent" | "delivered" | "read";
    liked?: boolean;
    isNew?: boolean; // 새 메시지 표시용
}

/**
 * 채팅 UI 컴포넌트
 */
export function ChatUI() {
    // messages 변수는 실제로 사용되지 않으므로 제거하고 초기화 로직만 유지
    const [visibleMessages, setVisibleMessages] = useState<ChatMessage[]>([]);
    // currentIndex 대신 언더스코어를 사용해 의도적으로 사용하지 않음을 표시
    const [, setCurrentIndex] = useState<number>(0);
    const [inputValue, setInputValue] = useState<string>("");
    const [isTyping, setIsTyping] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const { toast } = useToast();

    // 프로필 이미지 URL
    const PROFILE_IMAGE_URL = "https://ik.imagekit.io/u9hycyuth/test/%E1%84%91%E1%85%A1%E1%84%85%E1%85%B3%E1%84%90%E1%85%A6%E1%84%82%E1%85%A9%E1%86%AB2.jpeg?updatedAt=1747716262676";

    // 채팅 속도 조절 상수
    const TYPING_ANIMATION_DURATION = 3000; // 타이핑 표시 지속 시간 (ms)
    const MESSAGE_MIN_DELAY = 2000; // 메시지 사이 최소 지연 시간 (ms)
    const MESSAGE_RANDOM_DELAY = 1500; // 메시지 사이 랜덤 추가 지연 시간 (ms)
    const CONVERSATION_RESET_DELAY = 12000; // 대화 초기화 전 대기 시간 (ms)

    // 한 화면에 표시할 최대 메시지 수 (스크롤 없이 표시하기 위함)
    const MAX_VISIBLE_MESSAGES = 4;

    // 원본 채팅 데이터 (사용자가 제공한 콤마로 구분된 채팅)
    const rawMessagesString = "오 인스타 잘 보고 있어요 몸 진짜 좋으시던데요,아 넹 감사해요 운동 좀 해요 ㅎㅎ,저도 운동 좋아하는데 혹시 운동 루틴 공유 해주실 수 있나요?,그럼요 제가 한번 정리해서 알려드릴게요! 디엠으로 편하게 물어보세요,와 감사합니다 예진님은 운동 몇년 하셨어요?,한 5년 정도 꾸준히 하고 있어요 ㅎㅎ,와 진짜 대단하시네요 저는 아직 헬린이인데 부럽네요,아니에요 헬린이 시절이 제일 재밌을 때에요 꾸준히 하면 저보다 더 좋아지실거에요,좋은 말씀 감사합니다 혹시 운동하면서 힘든 점은 없으세요?,음.. 부상 조심해야 하고 식단 관리 꾸준히 하는 게 좀 힘들죠 ㅠㅠ";

    const rawMessages = rawMessagesString.split(",");

    useEffect(() => {
        // 초기 메시지 생성 (타임스탬프 추가)
        const now = new Date();
        const initialMessages = rawMessages.map((text, index) => {
            const messageTime = new Date(now);
            messageTime.setMinutes(now.getMinutes() - (rawMessages.length - index) * 5);

            return {
                id: `msg-${index}`,
                text,
                isMe: index % 2 === 0, // 첫 메시지는 나(오른쪽), 그 다음은 상대방(왼쪽) 식으로 번갈아가며 표시
                timestamp: messageTime,
                status: "read" as const,
                liked: false,
                isNew: true // 애니메이션용 플래그
            };
        });

        // 메시지를 하나씩 추가하는 애니메이션
        let timeoutId: NodeJS.Timeout;
        const animateMessages = (index: number) => {
            if (index < initialMessages.length) {
                // 메시지를 추가하되 최대 표시 개수를 유지
                setVisibleMessages(prev => {
                    // 새 메시지를 추가한 후 가장 오래된 메시지부터 제거하여 최대 개수 유지
                    const updatedMessages = [...prev, initialMessages[index]];
                    return updatedMessages.slice(Math.max(0, updatedMessages.length - MAX_VISIBLE_MESSAGES));
                });
                setCurrentIndex(index);

                // 메시지가 표시된 후 isNew 플래그 제거 (애니메이션 효과 위함)
                setTimeout(() => {
                    setVisibleMessages(prev =>
                        prev.map(msg =>
                            msg.id === initialMessages[index].id ? { ...msg, isNew: false } : msg
                        )
                    );
                }, 1000);

                // 상대방 메시지 이후에는 타이핑 표시 애니메이션
                if (!initialMessages[index].isMe && index + 1 < initialMessages.length && !initialMessages[index + 1].isMe) {
                    setIsTyping(true);
                    timeoutId = setTimeout(() => {
                        setIsTyping(false);
                        animateMessages(index + 1);
                    }, TYPING_ANIMATION_DURATION);
                } else {
                    // 다음 메시지 애니메이션 예약
                    timeoutId = setTimeout(() => {
                        setIsTyping(false);
                        animateMessages(index + 1);
                    }, MESSAGE_MIN_DELAY + Math.random() * MESSAGE_RANDOM_DELAY);
                }
            } else {
                // 모든 메시지 표시 후 잠시 대기한 다음 처음부터 다시 시작
                setIsTyping(false);
                timeoutId = setTimeout(() => {
                    setVisibleMessages([]);
                    setCurrentIndex(0);
                    timeoutId = setTimeout(() => {
                        animateMessages(0);
                    }, 1500); // 초기화 후 시작 지연
                }, CONVERSATION_RESET_DELAY);
            }
        };

        // 애니메이션 시작
        animateMessages(0);

        // 컴포넌트 언마운트 시 타이머 정리
        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [TYPING_ANIMATION_DURATION, MESSAGE_MIN_DELAY, MESSAGE_RANDOM_DELAY, CONVERSATION_RESET_DELAY]);

    // 메시지 시간 포맷팅
    const formatMessageTime = (date: Date): string => {
        return `${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
    };

    // 메시지 전송 처리 (전시용으로 비활성화)
    const handleSendMessage = (e?: React.FormEvent): void => {
        if (e) e.preventDefault();

        // 전시용이므로 실제 메시지 전송 대신 토스트 메시지로 알림 표시
        if (inputValue.trim()) {
            showDemoToast();
        }

        // 입력창 초기화
        setInputValue("");

        // 포커스 유지
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    // 메시지 좋아요 토글
    const toggleLike = (messageId: string): void => {
        setVisibleMessages(prev =>
            prev.map(msg =>
                msg.id === messageId ? { ...msg, liked: !msg.liked } : msg
            )
        );
    };

    // 토스트 메시지로 알림 표시
    const showDemoToast = (): void => {
        toast({
            title: "전시용 UI",
            description: "실제 메시지 전송은 비활성화되어 있습니다.",
            variant: "default",
            duration: 2000
        });
    };

    // 키보드 이벤트 핸들러 (Enter로 메시지 전송)
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <div className="instagram-chat-container">
            {/* 헤더 */}
            <div className="instagram-chat-header">
                <div className="profile-info">
                    <motion.img
                        src={PROFILE_IMAGE_URL}
                        alt="예진"
                        className="profile-pic"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.3 }}
                        whileHover={{ scale: 1.05 }}
                    />
                    <div className="profile-details">
                        <motion.span
                            className="profile-name"
                            initial={{ y: -5, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.3, delay: 0.1 }}
                        >
                            예진
                        </motion.span>
                        <motion.div
                            className="profile-status-container"
                            initial={{ y: 5, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.3, delay: 0.2 }}
                        >
                            <span className="profile-active-dot"></span>
                            <span className="profile-status">활동중</span>
                        </motion.div>
                    </div>
                </div>
                <div className="chat-actions">
                    <motion.button
                        className="action-button phone"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        onClick={showDemoToast}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                        </svg>
                    </motion.button>
                    <motion.button
                        className="action-button video"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        onClick={showDemoToast}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="23 7 16 12 23 17 23 7"></polygon>
                            <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                        </svg>
                    </motion.button>
                    <motion.button
                        className="action-button info"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        onClick={showDemoToast}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="16" x2="12" y2="12"></line>
                            <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                    </motion.button>
                </div>
            </div>

            {/* 메시지 영역 */}
            <div className="instagram-chat-wrapper" ref={containerRef}>
                <div className="fade-top"></div>
                <div className="messages">
                    <AnimatePresence mode="popLayout">
                        {visibleMessages.map((message) => (
                            <motion.div
                                key={message.id}
                                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                                animate={{
                                    opacity: 1,
                                    y: 0,
                                    scale: 1,
                                    transition: {
                                        duration: 0.7,
                                        type: "spring",
                                        stiffness: 80,
                                        damping: 8
                                    }
                                }}
                                exit={{
                                    opacity: 0,
                                    y: -20,
                                    transition: { duration: 0.3 }
                                }}
                                className={`message-row ${message.isMe ? 'message-right' : 'message-left'} ${message.isNew ? 'message-new' : ''}`}
                            >
                                {!message.isMe && (
                                    <div className="profile-image">
                                        <img src={PROFILE_IMAGE_URL} alt="Profile" />
                                    </div>
                                )}
                                <div className="message-content">
                                    <div className="message-bubble-wrapper">
                                        <motion.div
                                            className={`message-bubble ${message.isMe ? 'message-bubble-right' : 'message-bubble-left'}`}
                                            whileHover={{ scale: 1.02 }}
                                            onDoubleClick={() => toggleLike(message.id)}
                                            initial={message.isNew ? { scale: 0.9 } : { scale: 1 }}
                                            animate={{ scale: 1 }}
                                            transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                        >
                                            {message.text}
                                            {message.liked && (
                                                <motion.div
                                                    className="message-heart"
                                                    initial={{ scale: 0, opacity: 0 }}
                                                    animate={{ scale: 1, opacity: 1 }}
                                                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                                >
                                                    <Heart size={14} fill="#ed4956" color="#ed4956" />
                                                </motion.div>
                                            )}
                                        </motion.div>
                                        <div className="message-info">
                                            <span className="message-time">
                                                {formatMessageTime(message.timestamp)}
                                            </span>
                                            {message.isMe && message.status && (
                                                <span className={`message-status message-status-${message.status}`}>
                                                    {message.status === "sent" && "전송됨"}
                                                    {message.status === "delivered" && "전달됨"}
                                                    {message.status === "read" && "읽음"}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}

                        {/* 타이핑 표시 애니메이션 */}
                        {isTyping && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="message-row message-left"
                                key="typing-indicator"
                            >
                                <div className="profile-image">
                                    <img src={PROFILE_IMAGE_URL} alt="Profile" />
                                </div>
                                <div className="message-content">
                                    <div className="message-bubble-wrapper">
                                        <div className="message-bubble message-bubble-left typing-indicator">
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
                <div className="fade-bottom"></div>
            </div>

            {/* 입력 영역 */}
            <form onSubmit={handleSendMessage} className="instagram-chat-input">
                <div className="input-actions">
                    <motion.button
                        type="button"
                        className="action-icon-button"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={showDemoToast}
                    >
                        <Camera size={20} />
                    </motion.button>
                    <motion.button
                        type="button"
                        className="action-icon-button"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={showDemoToast}
                    >
                        <Image size={20} />
                    </motion.button>
                    <motion.button
                        type="button"
                        className="action-icon-button"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={showDemoToast}
                    >
                        <Mic size={20} />
                    </motion.button>
                </div>
                <div className="input-container">
                    <motion.button
                        type="button"
                        className="emoji-button"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={showDemoToast}
                    >
                        <Smile size={20} />
                    </motion.button>
                    <input
                        type="text"
                        placeholder="메시지 입력... (전시용)"
                        className="message-input"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        ref={inputRef}
                        aria-label="채팅 메시지 입력"
                    />
                    {inputValue.trim() ? (
                        <motion.button
                            type="submit"
                            className="send-button"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 500, damping: 20 }}
                            aria-label="메시지 보내기"
                        >
                            <Send size={20} />
                        </motion.button>
                    ) : (
                        <motion.button
                            type="button"
                            className="like-button"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={showDemoToast}
                            aria-label="좋아요 보내기"
                        >
                            <Heart size={20} />
                        </motion.button>
                    )}
                </div>
            </form>
        </div>
    );
} 